import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { getMyTeam, getAnalytics } from "../api";
import AppHeader from "../components/AppHeader";
import StatCard from "../components/StatCard";
import TeamlessPanel from "../components/TeamlessPanel";
import TrendLineChart from "../components/analytics/TrendLineChart";
import WorkloadBarChart from "../components/analytics/WorkloadBarChart";
import AgingPrTable from "../components/analytics/AgingPrTable";
import ChartEmptyState from "../components/analytics/ChartEmptyState";
import { chartColors } from "../components/analytics/chartTheme";

const RANGE_OPTIONS = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

function AnalyticsPage() {
  const { user, hasTeam, logout } = useAuth();
  const [rangeDays, setRangeDays] = useState(30);
  const [team, setTeam] = useState(null);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUpgradeRequired(false);
    try {
      // ⚠️ The analytics outcome is captured as a VALUE, never rethrown inside
      // Promise.all. It used to rethrow, which meant a failing analytics call
      // rejected the whole Promise.all and threw away the team result — even
      // though the team fetch has its own .catch and had already succeeded.
      //
      // That silently cost the teamless surface its data: a user whose team is
      // pending deletion would lose the Restore button on this page whenever
      // analytics errored, and be told "Analytics starts when your team does"
      // instead — with no way back to their team from here. Two independent
      // requests should fail independently.
      const [teamRes, analyticsOutcome] = await Promise.all([
        getMyTeam().catch(() => null),
        // Item 9: analytics is a real 403 for Free-tier teams, not a soft
        // degrade — classified here so it renders an upgrade prompt instead of
        // a raw "Request failed with status code 403".
        getAnalytics({ days: rangeDays })
          .then((res) => ({ ok: true, res }))
          .catch((err) => ({ ok: false, err })),
      ]);

      // Team state first, and unconditionally — it is what the teamless and
      // pending-deletion branches render from.
      if (teamRes) {
        setTeam(teamRes.data?.data?.team || null);
        setPendingDeletion(teamRes.data?.data?.pending_deletion || null);
      }

      if (analyticsOutcome.ok) {
        setAnalytics(analyticsOutcome.res.data?.data || null);
      } else if (
        analyticsOutcome.err.response?.status === 403 &&
        analyticsOutcome.err.response?.data?.data?.upgrade_required
      ) {
        setUpgradeRequired(true);
      } else {
        throw analyticsOutcome.err;
      }
    } catch (err) {
      setError("Failed to load analytics: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [rangeDays]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Teamless used to <Navigate> back to /dashboard from here, which made this
  // a link in the header that silently did nothing. It renders its own state
  // now, so the whole app stays walkable with no team.
  //
  // Deliberately placed BEFORE the loading return: the team answer is already
  // known, and there is nothing worth waiting on — a teamless user's analytics
  // request cannot produce data.
  if (hasTeam === false) {
    return (
      <div className="min-h-screen bg-rf-bg-primary">
        <AppHeader user={user} onLogout={logout} active="analytics" />
        <main className="max-w-[1200px] mx-auto px-4 sm:px-8 py-7">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-rf-text-primary tracking-tight mb-1">
              Analytics
            </h1>
            {/*
              No range chips. A 7/30/90-day selector over no team is three
              controls that cannot change anything.
            */}
            <p className="text-sm text-rf-text-secondary">No team yet</p>
          </div>
          {/*
            Same card shape as the upgrade prompt below, deliberately — this
            page already had a "you can't see this yet, here's why" pattern
            and inventing a second one would make the two states look like
            different kinds of problem.

            ⚠️ This branch MUST stay above the upgradeRequired one. Both gates
            can be true at once: a teamless user is also, technically, not on
            Pro. Checking Pro first would tell someone with no team to
            "Upgrade to Pro" — asking for money to fix a problem they do not
            have. Team first, always.

            No skeleton charts behind this card either. Skeletons mean
            "loading" here and everywhere else in the app, so using them for
            "nothing yet" would signal work in progress that never finishes.
          */}
          <div className="max-w-[720px]">
            <TeamlessPanel
              pendingDeletion={pendingDeletion}
              title="Analytics starts when your team does"
              body="Once you're on a team, this page shows pull request volume and complexity over time, how review load is spread across reviewers, and which pull requests have been waiting longest."
            />
          </div>
        </main>
      </div>
    );
  }

  if (loading && !analytics) {
    return (
      <div className="min-h-screen bg-rf-bg-primary flex items-center justify-center">
        <div className="text-rf-text-secondary text-sm">Loading analytics...</div>
      </div>
    );
  }

  const summary = analytics?.summary;
  const trend = analytics?.trend || [];
  const meta = analytics?.meta;
  const netBacklogChange = summary?.net_backlog_change ?? 0;

  return (
    <div className="min-h-screen bg-rf-bg-primary">
      <AppHeader user={user} onLogout={logout} plan={team?.plan} active="analytics" />

      <main className="max-w-[1200px] mx-auto px-8 py-7">
        <div className="mb-7 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-rf-text-primary tracking-tight mb-1">
              Team Analytics
            </h1>
            <p className="text-sm text-rf-text-secondary">
              {team?.team_name || "Team"} · last {rangeDays} days
              {meta?.first_recorded_date && ` · data since ${meta.first_recorded_date}`}
            </p>
          </div>
          <div className="flex gap-1.5">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setRangeDays(opt.key)}
                className="px-3 py-2 rounded-md text-xs font-medium transition-all duration-200"
                style={{
                  border: `1px solid ${rangeDays === opt.key ? chartColors.blue : "rgba(30,40,64,0.4)"}`,
                  background: rangeDays === opt.key ? `${chartColors.blue}18` : "transparent",
                  color: rangeDays === opt.key ? chartColors.blue : chartColors.textSecondary,
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-rf-accent-danger/10 border border-rf-accent-danger/30 text-rf-accent-danger text-sm">
            {error}
            <button onClick={fetchData} className="ml-3 underline hover:no-underline">
              Retry
            </button>
          </div>
        )}

        {upgradeRequired ? (
          <div className="bg-rf-bg-secondary rounded-xl p-10 text-center border border-rf-border">
            <h2 className="text-lg font-semibold text-rf-text-primary mb-2">
              Team analytics is a Pro feature
            </h2>
            <p className="text-sm text-rf-text-secondary mb-5">
              Upgrade to Pro to see PR volume trends, complexity over time, reviewer workload, and aging PRs.
            </p>
            <Link
              to="/team"
              className="inline-block px-5 py-2.5 rounded-lg bg-rf-accent text-white font-semibold text-sm hover:bg-rf-accent/90 transition-colors"
            >
              Upgrade to Pro →
            </Link>
          </div>
        ) : (
          <>
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard label="PRs Opened" value={summary.prs_opened} accent={chartColors.blue} />
            <StatCard
              label="PRs Resolved"
              value={summary.prs_resolved}
              sub={`${summary.prs_merged} merged · ${summary.prs_closed} closed`}
              accent={chartColors.accent}
            />
            <StatCard
              label="Net Backlog Change"
              value={netBacklogChange > 0 ? `+${netBacklogChange}` : netBacklogChange}
              sub={netBacklogChange > 0 ? "growing" : netBacklogChange < 0 ? "shrinking" : "flat"}
              accent={netBacklogChange > 0 ? chartColors.danger : chartColors.accent}
            />
            <StatCard
              label="Avg Complexity"
              value={summary.avg_complexity !== null ? summary.avg_complexity.toFixed(1) : "—"}
              sub="/10.0"
              accent={chartColors.purple}
            />
          </div>
        )}

        <ChartEmptyState trend={trend} firstRecordedDate={meta?.first_recorded_date} />

        <div className="bg-rf-bg-secondary rounded-xl p-6 mb-6">
          <h2 className="text-base font-semibold text-rf-text-primary mb-4">
            PR Volume vs. Resolution
          </h2>
          <TrendLineChart
            data={trend}
            ariaLabel="Daily count of PRs opened versus PRs resolved (merged or closed)"
            series={[
              { dataKey: "prs_opened", label: "Opened", color: chartColors.blue },
              { dataKey: "prs_resolved", label: "Resolved", color: chartColors.accent, dashed: true },
            ]}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-rf-bg-secondary rounded-xl p-6">
            <h2 className="text-base font-semibold text-rf-text-primary mb-4">
              Complexity Trend
            </h2>
            <TrendLineChart
              data={trend}
              ariaLabel="Daily average PR complexity score, on a 0 to 10 scale"
              yDomain={[0, 10]}
              series={[
                // connectNulls: true — days with no analyzed PRs (avg_complexity
                // is null, not zero) previously broke the line into disconnected
                // segments; the trend now draws through those gaps instead.
                { dataKey: "avg_complexity", label: "Avg complexity", color: chartColors.purple, connectNulls: true },
              ]}
            />
          </div>

          <div className="bg-rf-bg-secondary rounded-xl p-6">
            <h2 className="text-base font-semibold text-rf-text-primary mb-4">
              Reviewer Workload
            </h2>
            {analytics?.reviewer_workload?.length > 0 ? (
              <WorkloadBarChart
                data={analytics.reviewer_workload}
                ariaLabel="Active review count per reviewer"
              />
            ) : (
              <div className="text-center py-8 text-rf-text-muted text-sm">
                No reviewers found
              </div>
            )}
          </div>
        </div>

        <div className="bg-rf-bg-secondary rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-base font-semibold text-rf-text-primary">
              Aging PRs
            </h2>
            <span className="text-xs text-rf-text-muted">
              open longer than {meta?.stale_after_days ?? 7} days
            </span>
          </div>
          <AgingPrTable data={analytics?.aging_prs || []} />
        </div>
          </>
        )}
      </main>
    </div>
  );
}

export default AnalyticsPage;
